import { Skeleton } from '@mantine/core';
import { IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import { useConfirmWithMessage } from 'components/modals/confirm-with-message/ConfirmWithMessageProvider';
import BulkConfirmMessage from 'components/tables/data/price-offer/actions/bulk/BulkConfirmMessage';
import { PriceOfferBulkActionProps } from 'components/tables/data/price-offer/types';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useMemo, useState } from 'react';

/**
 * Performs a bulk delete on all selected actionable price offers.
 */
export default function BulkDeleteAction({ priceOffers, onBulkActionComplete = noop }: PriceOfferBulkActionProps) {
  const { getAction } = useApi();
  const { confirmWithMessage } = useConfirmWithMessage();
  const [loading, setLoading] = useState(false);

  const availablePriceOffers = useMemo(
    () =>
      priceOffers.filter(
        (priceOffer) =>
          priceOffer.actionPermissions.canDelete &&
          !['acceptable', 'unacceptable', 'deleted'].includes(priceOffer.status.slug)
      ),
    [priceOffers]
  );

  /**
   * Deletes all selected actionable price offers.
   */
  const deleteAll = () =>
    confirmWithMessage({
      title: 'Zmazať cenové ponuky',
      message: (
        <BulkConfirmMessage
          message="Naozaj si prajete zmazať vybrané cenové ponuky?"
          priceOffers={availablePriceOffers}
        />
      ),
      reasonLabel: 'Poznámka',
      primaryButtonText: 'Zmazať',
      secondaryButtonText: 'Zrušiť',
      onConfirmWithMessage: async (note?: string) => {
        try {
          setLoading(true);
          const action = getAction('PriceOfferDelete');

          for (const priceOffer of availablePriceOffers) {
            await action({
              parameters: { priceOfferId: String(priceOffer.priceOfferId) },
              payload: { note },
            });
          }

          onBulkActionComplete('delete', availablePriceOffers);
        } catch (error) {
          panic(error as Error);
        } finally {
          setLoading(false);
        }
      },
    });

  if (loading) {
    return <Skeleton w={120} h={32} radius={4} />;
  }

  if (availablePriceOffers.length === 0) {
    return <></>;
  }

  return (
    <Button variant="danger-secondary" size="sm" leftIcon={<IconTrashX />} onClick={deleteAll}>
      Zmazať ({availablePriceOffers.length})
    </Button>
  );
}
